import React, { useCallback, useEffect, useState } from 'react';
import { Button, Tabs, Tag, Modal, Input, Select, Spin, Empty } from 'antd';
import { CheckOutlined, CloseOutlined, RollbackOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { userRequest } from '../../config';
import { NdoePage, NdoePageHeader, NdoeError } from '../../components/ndoe';
import './validationDepenses.css';

/*
  Validation des feuilles de dépense — écran de la secrétaire et de l'admin.

  Le cas NORMAL est « Tout valider » en un clic : sur les rapports analysés,
  88 % des journées sont arithmétiquement justes. Les alertes ne bloquent rien,
  elles attirent l'œil — une feuille sans alerte d'attention ni critique est
  marquée « propre » par le serveur et se valide sans remords.

  Le second onglet est la file de supervision des lieux saisis librement.
  Elle est structurelle, pas exceptionnelle : ~14 % des trajets touchent un lieu
  absent du référentiel, et ce taux ne décroît pas avec le temps.

  Piège du dépôt : aucun sous-composant défini dans le rendu (focus perdu).
*/

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const COULEUR_ALERTE = { critique: 'error', attention: 'warning', info: 'default' };

// Pastille de synthèse d'une feuille dans la file.
const pastilleAlertes = (a) => {
  if (!a || a.total === 0) return <Tag color="success">propre</Tag>;
  if (a.critique) return <Tag color="error">{a.critique} à vérifier</Tag>;
  if (a.attention) return <Tag color="warning">{a.attention} à regarder</Tag>;
  return <Tag color="default">{a.info} info</Tag>;
};

const ValidationDepenses = () => {
  const [onglet, setOnglet] = useState('feuilles');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const [feuilles, setFeuilles] = useState([]);
  const [selection, setSelection] = useState(null);   // détail d'une feuille
  const [demandes, setDemandes] = useState([]);
  const [points, setPoints] = useState([]);
  const [cibles, setCibles] = useState({});           // id_demande -> id_point choisi

  const charger = useCallback(async () => {
    try {
      setErreur(null);
      const [f, p] = await Promise.all([
        userRequest.get('/api/feuille-depense/a-valider'),
        userRequest.get('/api/feuille-depense/points-a-rattacher'),
      ]);
      setFeuilles(f.data?.feuilles || []);
      setDemandes(p.data?.demandes || []);
      setPoints(p.data?.points || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible.');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirDetail = async (id) => {
    setEnCours(true);
    try {
      const { data } = await userRequest.get(`/api/feuille-depense/a-valider/${id}`);
      setSelection(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Détail indisponible.');
    } finally {
      setEnCours(false);
    }
  };

  const rafraichirApres = async (message) => {
    setSelection(null);
    await charger();
    toast.success(message);
  };

  const validerTout = (f) => Modal.confirm({
    title: 'Valider toute la feuille ?',
    content: `${f.declarant} · ${f.date} · ${fmt(f.total_fc)} FC. Les dépenses seront enregistrées en comptabilité.`,
    okText: 'Valider', cancelText: 'Annuler',
    onOk: async () => {
      try {
        const { data } = await userRequest.put(`/api/feuille-depense/${f.id_feuille}/valider`);
        await rafraichirApres(`${data.ecritures?.length || 0} écriture(s) enregistrée(s).`);
      } catch (e) {
        toast.error(e.response?.data?.error || 'Validation impossible.');
      }
    },
  });

  const renvoyer = (f) => {
    let motif = '';
    Modal.confirm({
      title: 'Renvoyer au déclarant',
      content: (
        <div>
          <p className="vd-modal-aide">Indiquez ce qu’il doit corriger — c’est la seule information qu’il recevra.</p>
          <Input.TextArea rows={3} maxLength={255} onChange={(e) => { motif = e.target.value; }} />
        </div>
      ),
      okText: 'Renvoyer', cancelText: 'Annuler', okButtonProps: { danger: true },
      onOk: async () => {
        if (!motif.trim()) { toast.error('Le motif est obligatoire.'); throw new Error('motif vide'); }
        try {
          await userRequest.put(`/api/feuille-depense/${f.id_feuille}/renvoyer`, { motif });
          await rafraichirApres('Feuille renvoyée au déclarant.');
        } catch (e) {
          toast.error(e.response?.data?.error || 'Renvoi impossible.');
          throw e;
        }
      },
    });
  };

  const statuerLigne = async (idLigne, decision, motif) => {
    try {
      await userRequest.put(`/api/feuille-depense/ligne/${idLigne}/statuer`, { decision, motif });
      await ouvrirDetail(selection.feuille.id_feuille);
      await charger();
      toast.success(decision === 'REJETEE' ? 'Ligne rejetée.' : 'Ligne validée.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action impossible.');
    }
  };

  const rejeterLigne = (l) => {
    let motif = '';
    Modal.confirm({
      title: 'Rejeter cette ligne',
      content: (
        <div>
          <p className="vd-modal-aide">{l.depart} → {l.arrivee} · {fmt(l.montant_fc)} FC</p>
          <Input.TextArea rows={2} maxLength={200} placeholder="Pourquoi ?" onChange={(e) => { motif = e.target.value; }} />
        </div>
      ),
      okText: 'Rejeter', cancelText: 'Annuler', okButtonProps: { danger: true },
      onOk: async () => {
        if (!motif.trim()) { toast.error('Le motif est obligatoire.'); throw new Error('motif vide'); }
        await statuerLigne(l.id_ligne, 'REJETEE', motif);
      },
    });
  };

  const resoudrePoint = async (d, promouvoir) => {
    setEnCours(true);
    try {
      const corps = promouvoir ? {} : { id_point: cibles[d.id_demande] };
      const { data } = await userRequest.put(`/api/feuille-depense/points-a-rattacher/${d.id_demande}/resoudre`, corps);
      await charger();
      toast.success(data.mode === 'PROMU'
        ? `« ${data.libelle} » ajouté au référentiel.`
        : `« ${data.libelle} » rattaché · ${data.lignes_mises_a_jour} ligne(s) corrigée(s).`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Résolution impossible.');
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) return <NdoePage><div className="vd-centre"><Spin size="large" /></div></NdoePage>;
  if (erreur) return <NdoePage><NdoeError text={erreur} onRetry={charger} /></NdoePage>;

  return (
    <NdoePage className="validation-depenses">
      <NdoePageHeader
        eyebrow="Dépenses"
        title="Feuilles à valider"
        subtitle="Les dépenses déclarées entrent en comptabilité une fois validées ici."
        actions={<Button icon={<ReloadOutlined />} onClick={charger}>Rafraîchir</Button>}
      />

      <Tabs
        activeKey={onglet}
        onChange={setOnglet}
        items={[
          { key: 'feuilles', label: `À valider (${feuilles.length})` },
          { key: 'points', label: `Lieux à rattacher (${demandes.length})` },
        ]}
      />

      {onglet === 'feuilles' && !selection ? (
        <section>
          {feuilles.length === 0 ? (
            <Empty description="Aucune feuille en attente." />
          ) : (
            <ul className="vd-liste">
              {feuilles.map((f) => (
                <li key={f.id_feuille} className={`vd-feuille${f.alertes?.propre ? '' : ' vd-feuille--signalee'}`}>
                  <div className="vd-feuille-texte">
                    <span className="vd-feuille-titre">{f.declarant} · {f.date}</span>
                    <span className="vd-feuille-detail">
                      {f.nb_lignes} ligne(s) · {fmt(f.total_fc)} FC {pastilleAlertes(f.alertes)}
                    </span>
                  </div>
                  <div className="vd-feuille-actions">
                    <Button size="small" onClick={() => ouvrirDetail(f.id_feuille)} disabled={enCours}>Détail</Button>
                    <Button size="small" icon={<RollbackOutlined />} onClick={() => renvoyer(f)} disabled={enCours}>Renvoyer</Button>
                    <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => validerTout(f)} disabled={enCours}>
                      Tout valider
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {onglet === 'feuilles' && selection ? (
        <section className="vd-detail">
          <div className="vd-detail-tete">
            <Button size="small" onClick={() => setSelection(null)}>← Retour à la file</Button>
            <span className="vd-detail-titre">
              {selection.feuille.declarant} · {selection.feuille.date} · <strong>{fmt(selection.feuille.total_fc)} FC</strong>
            </span>
          </div>

          <ul className="vd-lignes">
            {selection.lignes.map((l) => (
              <li key={l.id_ligne} className={`vd-ligne vd-ligne--${(l.statut || '').toLowerCase()}`}>
                <div className="vd-ligne-texte">
                  <span className="vd-ligne-trajet">
                    {l.depart || l.arrivee ? `${l.depart || '?'} → ${l.arrivee || '?'}` : 'Forfait téléphone'}
                  </span>
                  <span className="vd-ligne-detail">
                    {[l.moyen, l.nom_client, l.type].filter(Boolean).join(' · ')}
                  </span>
                  {(l.alertes || []).map((a, i) => (
                    <Tag key={`${l.id_ligne}-${i}`} color={COULEUR_ALERTE[a.niveau]} className="vd-alerte">{a.message}</Tag>
                  ))}
                  {l.motif_rejet ? <span className="vd-motif">Rejetée : {l.motif_rejet}</span> : null}
                </div>
                <span className="vd-ligne-montant">{fmt(l.montant_fc)} FC</span>
                {l.statut !== 'REJETEE' ? (
                  <Button size="small" danger icon={<CloseOutlined />} onClick={() => rejeterLigne(l)} aria-label="Rejeter la ligne" />
                ) : (
                  <Button size="small" icon={<CheckOutlined />} onClick={() => statuerLigne(l.id_ligne, 'VALIDEE')} aria-label="Rétablir la ligne" />
                )}
              </li>
            ))}
          </ul>

          <div className="vd-detail-actions">
            <Button icon={<RollbackOutlined />} onClick={() => renvoyer(selection.feuille)}>Renvoyer au déclarant</Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => validerTout(selection.feuille)}>
              Valider la feuille
            </Button>
          </div>
        </section>
      ) : null}

      {onglet === 'points' ? (
        <section>
          <p className="vd-aide">
            Un déclarant qui ne trouve pas son lieu le saisit librement : sa dépense est enregistrée
            sans attendre. À vous de le rattacher à un lieu existant, ou de l’ajouter au référentiel.
            Un arbitrage vaut pour toutes les occurrences du même nom.
          </p>
          {demandes.length === 0 ? (
            <Empty description="Aucun lieu en attente." />
          ) : (
            <ul className="vd-liste">
              {demandes.map((d) => (
                <li key={d.id_demande} className="vd-demande">
                  <div className="vd-demande-texte">
                    <span className="vd-demande-libelle">« {d.libelle} »</span>
                    <span className="vd-feuille-detail">
                      {d.declarant} · {d.occurrences > 1 ? `${d.occurrences} occurrences` : '1 occurrence'}
                    </span>
                  </div>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Rattacher à…"
                    className="vd-cible"
                    value={cibles[d.id_demande]}
                    onChange={(v) => setCibles((c) => ({ ...c, [d.id_demande]: v }))}
                    options={points.map((p) => ({ value: p.id_point, label: p.nom }))}
                  />
                  <Button
                    size="small" type="primary" disabled={!cibles[d.id_demande] || enCours}
                    onClick={() => resoudrePoint(d, false)}
                  >
                    Rattacher
                  </Button>
                  <Button size="small" disabled={enCours} onClick={() => resoudrePoint(d, true)}>
                    En faire un lieu
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </NdoePage>
  );
};

export default ValidationDepenses;
